import {
  Fragment,
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

// import ReactDOM from "react-dom";
import invariant from "tiny-invariant";

import FocusRing from "@atlaskit/focus-ring";
import ChevronDownIcon from "@atlaskit/icon/glyph/chevron-down";
import ChevronRightIcon from "@atlaskit/icon/glyph/chevron-right";
import { ModalTransition } from "@atlaskit/modal-dialog";
import {
  type Instruction,
  type ItemMode,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
  ElementDropTargetEventBasePayload,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { pointerOutsideOfPreview } from "@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import type { DragLocationHistory } from "@atlaskit/pragmatic-drag-and-drop/types";

import { type TreeItem as TreeItemType } from "../data/tree";

// import { indentPerLevel } from "./constants";
// import { MoveDialog } from "./move-dialog";
import { DependencyContext, TreeContext } from "./tree-context";
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { createRoot } from "react-dom/client";
import useStyles from "./styles";
import {
  AddCircleRounded,
  DeleteRounded,
  EditRounded,
  ImportExportRounded,
  IosShareRounded,
  MoreHoriz,
  OutputRounded,
  WebRounded,
} from "@mui/icons-material";
import { MoveModal } from "./moveModal";
import { RenameModal } from "./renameModal";
import { NewItemModal } from "./newItemModal";
import { useNotification } from "../../../notificationProvider";
import api from "../../../../services/api";
import NewAlertModal from "../../../newAlertModal";
import { Socket } from "socket.io-client";

// const iconColor = token("color.icon", "#44546F");

// function ChildIcon() {
//   return (
//     <svg aria-hidden={true} width={24} height={24} viewBox="0 0 24 24">
//       <circle cx={12} cy={12} r={2} fill={iconColor} />
//     </svg>
//   );
// }
function GroupIcon({ isOpen }: { isOpen: boolean }) {
  const Icon = isOpen ? ChevronDownIcon : ChevronRightIcon;
  const color = useTheme().palette.primary.main;
  return <Icon label="" primaryColor={color} />;
}

function Icon({ item }: { item: TreeItemType }) {
  if (!item.children.length) {
    return;
  }
  return <GroupIcon isOpen={item.isOpen ?? false} />;
}

function Preview({ item, theme }: { item: TreeItemType; theme: Theme }) {
  const styles = useStyles(theme);

  return (
    <Box sx={styles.previewStyles}>
      {item.type == "FILHO" ? (
        <WebRounded
          sx={{ color: theme.palette.text.secondary, mr: 1 }}
          fontSize="small"
        />
      ) : null}
      {item.title}
    </Box>
  );
}

function getParentLevelOfInstruction(instruction: Instruction): number {
  if (instruction.type === "instruction-blocked") {
    return getParentLevelOfInstruction(instruction.desired);
  }
  if (instruction.type === "reparent") {
    return instruction.desiredLevel - 1;
  }
  return instruction.currentLevel - 1;
}

function delay({
  waitMs: timeMs,
  fn,
}: {
  waitMs: number;
  fn: () => void;
}): () => void {
  let timeoutId: number | null = window.setTimeout(() => {
    timeoutId = null;
    fn();
  }, timeMs);
  return function cancel() {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
}

const TreeItem = memo(function TreeItem({
  item,
  mode,
  level,
  index,
  socket,
  setExpanded,
  setUpdatePages,
  handleUpdateStatus,
}: {
  item: TreeItemType;
  mode: ItemMode;
  level: number;
  index: number;
  socket: Socket;
  setExpanded: (value: "paginas-livres" | "lixeira" | false) => void;
  setUpdatePages: (value: boolean) => void;
  handleUpdateStatus: (params: { itemId: string; status: number }) => void;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const theme = useTheme();
  const indentPerLevel = 32;
  const [state, setState] = useState<
    "idle" | "dragging" | "preview" | "parent-of-instruction"
  >("idle");
  const [instruction, setInstruction] = useState<Instruction | null>(null);
  const cancelExpandRef = useRef<(() => void) | null>(null);

  const { dispatch, uniqueContextId, getPathToItem, registerTreeItem } =
    useContext(TreeContext);
  const { DropIndicator, attachInstruction, extractInstruction } =
    useContext(DependencyContext);
  const toggleOpen = useCallback(() => {
    dispatch({ type: "toggle", itemId: item.id });
  }, [dispatch, item]);
  const styles = useStyles(theme);
  const actionMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isNewItemDialogOpen, setIsNewItemDialogOpen] = useState(false);
  const [type, setType] = useState<"FILHO" | "PAI">("FILHO");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const notify = useNotification();
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // useEffect(() => {
  //   invariant(buttonRef.current);
  //   invariant(actionMenuTriggerRef.current);
  //   return registerTreeItem({
  //     itemId: item.id,
  //     element: buttonRef.current,
  //     actionMenuTrigger: actionMenuTriggerRef.current,
  //   });
  // }, [item.id, registerTreeItem]);

  const cancelExpand = useCallback(() => {
    cancelExpandRef.current?.();
    cancelExpandRef.current = null;
  }, []);

  const clearParentOfInstructionState = useCallback(() => {
    setState((current) =>
      current === "parent-of-instruction" ? "idle" : current
    );
  }, []);

  // When an item has an instruction applied
  // we are highlighting it's parent item for improved clarity
  /**codigo do atlassian */
  const shouldHighlightParent = useCallback(
    (location: DragLocationHistory): boolean => {
      const target = location.current.dropTargets[0];

      if (!target) {
        return false;
      }

      const instruction = extractInstruction(target.data);

      if (!instruction) {
        return false;
      }

      const targetId = target.data.id;
      invariant(typeof targetId === "string");

      const path = getPathToItem(targetId);
      const parentLevel: number = getParentLevelOfInstruction(instruction);
      const parentId = path[parentLevel];

      return parentId === item.id;
    },
    [getPathToItem, extractInstruction, item]
  );
  /**codigo do atlassian */
  useEffect(() => {
    // invariant(buttonRef.current);
    if (!buttonRef.current) {
      return;
    }
    function updateIsParentOfInstruction({
      location,
    }: {
      location: DragLocationHistory;
    }) {
      if (shouldHighlightParent(location)) {
        setState("parent-of-instruction");
        return;
      }
      clearParentOfInstructionState();
    }

    function onChange({ self, source }: ElementDropTargetEventBasePayload) {
      const instruction = extractInstruction(self.data);
      // console.log(instruction, "instruction");
      if (source.data.id !== item.id) {
        // expand after 500ms if still merging
        if (
          instruction?.type === "make-child" &&
          item.children.length &&
          !item.isOpen &&
          !cancelExpandRef.current
        ) {
          cancelExpandRef.current = delay({
            waitMs: 500,
            fn: () => dispatch({ type: "expand", itemId: item.id }),
          });
        }
        if (instruction?.type !== "make-child" && cancelExpandRef.current) {
          cancelExpand();
        }

        setInstruction(instruction);
        return;
      }
      if (instruction?.type === "reparent") {
        setInstruction(instruction);
        return;
      }
      setInstruction(null);
    }
    function getDataTarget({
      input,
      element,
    }: {
      input: any;
      element: Element;
    }) {
      const data = { id: item.id };

      return attachInstruction(data, {
        input,
        element,
        indentPerLevel,
        currentLevel: level,
        mode,
        block:
          item.type === "FILHO"
            ? ["make-child", "reorder-above", "reorder-below"]
            : ["reorder-above", "reorder-below"],
      });
    }
    function onDropTarget({
      self,
      source,
      location,
    }: ElementDropTargetEventBasePayload) {
      const instruction = extractInstruction(self.data);
      // if (instruction) {
      //   const path = getPathToItem(self.data.id as string);
      //   const parentLevel: number = getParentLevelOfInstruction(instruction);
      //   const parentId = path[parentLevel];
      //   const positionSelf = item.position;
      //   // if(instruction.type === "reorder-above") {
      //   //   handlSocketUpdatePosition(
      //   //     item.id,
      //   //     positionSelf,
      //   //     parentId,
      //   //     1
      //   //   );
      //   // console.log("asdsdad", positionSelf);

      //   // const data = handlSocketUpdatePosition(item.id, null, null, 1);
      // }

      cancelExpand();
      setInstruction(null);
      setUpdatePages(true);
      // return;
    }
    return combine(
      draggable({
        element: buttonRef.current,
        getInitialData: () => ({
          id: item.id,
          typeItem: item.type,
          type: "tree-item",
          isOpenOnDragStart: item.isOpen,
          uniqueContextId: "main-tree",
        }),
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          setCustomNativeDragPreview({
            getOffset: pointerOutsideOfPreview({ x: "16px", y: "8px" }),
            render: ({ container }) => {
              const root = createRoot(container);
              root.render(<Preview item={item} theme={theme} />);
              // ReactDOM.render(<Preview item={item} />, container);
              // return () => ReactDOM.unmountComponentAtNode(container);
              return () => root.unmount();
            },
            nativeSetDragImage,
          });
        },
        onDragStart: ({ source }) => {
          setState("dragging");
          // collapse open items during a drag
          if (source.data.isOpenOnDragStart) {
            dispatch({ type: "collapse", itemId: item.id });
          }
        },
        onDrop: ({ source }) => {
          setState("idle");
          if (source.data.isOpenOnDragStart) {
            dispatch({ type: "expand", itemId: item.id });
          }
        },
      }),
      dropTargetForElements({
        element: buttonRef.current,
        getData: getDataTarget,
        canDrop: ({ source }) => source.data.type === "tree-item",
        // source.data.uniqueContextId === uniqueContextId,
        getIsSticky: () => true,
        onDragEnter: onChange,
        onDrag: onChange,
        onDragLeave: () => {
          cancelExpand();
          setInstruction(null);
        },
        onDrop: onDropTarget,
      }),
      monitorForElements({
        canMonitor: ({ source }) => source.data.type === "tree-item",
        onDragStart: updateIsParentOfInstruction,
        onDrag: updateIsParentOfInstruction,
        onDrop() {
          clearParentOfInstructionState();
        },
      })
    );
  }, [
    dispatch,
    item,
    mode,
    level,
    cancelExpand,
    uniqueContextId,
    extractInstruction,
    attachInstruction,
    getPathToItem,
    clearParentOfInstructionState,
    shouldHighlightParent,
  ]);
  /**codigo do atlassian */
  useEffect(
    function mount() {
      return function unmount() {
        cancelExpand();
      };
    },
    [cancelExpand]
  );
  /**codigo do atlassian */
  const aria = (() => {
    if (!item.children.length) {
      return undefined;
    }
    return {
      "aria-expanded": item.isOpen,
      "aria-controls": `tree-item-${item.id}--subtree`,
    };
  })();

  const openMoveDialog = useCallback(() => {
    setIsMoveDialogOpen(true);
    handleClose();
  }, []);
  const closeMoveDialog = useCallback(() => {
    setIsMoveDialogOpen(false);
  }, []);
  const openRenameDialog = useCallback(() => {
    setIsRenameDialogOpen(true);
    handleClose();
  }, []);
  const closeRenameDialog = useCallback(() => {
    setIsRenameDialogOpen(false);
  }, []);
  const openNewItemDialog = useCallback(
    (type: "PAI" | "FILHO") => {
      setType(type);
      setIsNewItemDialogOpen(true);
      handleClose();
    },
    [type]
  );

  const closeNewItemDialog = useCallback(() => {
    setIsNewItemDialogOpen(false);
  }, []);
  const handleRemoveItemTreeView = useCallback(() => {
    handleUpdateStatus({
      itemId: item.id,
      status: 2, // 2 = LIVRES
    });
    handleClose();
  }, [dispatch, item, handleClose]);

  async function handleExportPageApi(
    id: string
  ): Promise<any | null | undefined> {
    try {
      setLoadingExport(true);
      const response = await api.post(
        "/pages/export",
        {
          id,
        }
        // { signal }
      );
      if (response.status === 200) {
        // notify("Página exportada com sucesso", "success");
        // console.log("Exported data:", response.data);
        return response.data;
      }
    } catch (error: any) {
      if (error.code && !error.response) {
        notify(error.code, error.message, "error");
      } else {
        notify(error.status, error?.response?.data.message, "error");
      }
      console.error(error);
    } finally {
      setLoadingExport(false);
    }
    return null;
  }
  const handleButtonExport = useCallback(async () => {
    handleClose();
    const data = await handleExportPageApi(item.id);
    if (!data) {
      notify(null, "Erro ao exportar página", "error");
      return;
    }
    try {
      const jsonString = JSON.stringify(data, null, 2);

      const blob = new Blob([jsonString], { type: "application/json" });

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = (data?.title || "paginaEasyBI") + ".json";

      link.click();

      // Libera o objeto URL após o download
      URL.revokeObjectURL(url);
      notify(null, "Página exportada com sucesso", "success");
    } catch (error) {
      notify(null, "Erro ao exportar página", "error");
    }
  }, [handleExportPageApi, notify]);
  async function handleApiDumpPage(
    id: string,
    status: number
  ): Promise<any | null | undefined> {
    try {
      // setLoadingDump(true);
      const response = await api.put("/pages/updateStatus", {
        id,
        status,
      });
      if (response.status === 200) {
        return response.data;
      }
    } catch (error: any) {
      if (error.code && !error.response) {
        notify(error.code, error.message, "error");
      } else {
        notify(error.status, error?.response?.data.message, "error");
      }
      // notify(error.status, "Erro ao exportar página", "error");
      console.error(error);
    } finally {
      // setLoadingExport(false);
    }
    return null;
  }
  const handleButtonDump = useCallback(async () => {
    handleUpdateStatus({
      itemId: item.id,
      status: 0,
    });
    handleClose();
  }, [item.id]);
  async function handleApiDeletePage(
    id: string
  ): Promise<any | null | undefined> {
    try {
      // setLoadingDump(true);
      const response = await api.put("/pages/delete", {
        id,
        status: 0,
      });
      if (response.status === 200) {
        return response.data;
      }
    } catch (error: any) {
      notify(null, "Erro ao remover página", "error");
      console.error(error);
    } finally {
      // setLoadingExport(false);
      setOpenDeleteDialog(false);
    }
    return null;
  }
  const responsiveHasPage = (item: TreeItemType) => {
    if (item.type === "FILHO") return true;

    if (!item.children.length) return false;

    return item.children.some((child) => {
      if (responsiveHasPage(child)) return true;
    });
  };
  const handleConfirmDelete = useCallback(async () => {
    const data = await handleApiDeletePage(item.id);
    if (!data) {
      notify(null, "Erro ao excluir página", "error");
      return;
    }
    // console.log("data", data, item);

    // console.log(responsiveHasPage(item), item, "responsiveHasPage");

    dispatch({ type: "remove", itemId: item.id });

    if (responsiveHasPage(item)) setExpanded("lixeira");
    notify(null, "Página excluída com sucesso", "success");
  }, [handleClose]);
  const handleButtonDelete = useCallback(async () => {
    handleClose();
    setOpenDeleteDialog(true);
  }, [handleClose]);

  // console.log(socket.connected, "socket conectado");

  return (
    <Fragment>
      <Box
        sx={[
          styles.outerStyles,
          state === "idle" ? styles.outerHoverStyles : null,
        ]}
        // style={{ position: "relative" }}
      >
        <FocusRing isInset>
          {/* <Tooltip
            title={
              item.type === "FILHO"
                ? "Página " + item.title
                : "Item " + item.title
            }
            followCursor
            placement="right"
            slotProps={{
              tooltip: {
                sx: {
                  p: 0,
                  m: 0,
                  bgcolor: "transparent",
                },
              },
            }}
          > */}
          <button
            {...aria}
            style={{
              ...styles.outerButtonStyles(item, instruction),
              // paddingLeft: level * indentPerLevel,
              // ...(instruction &&
              // (instruction.type === "make-child" ||
              //   instruction.type === "reparent")
              //   ? { border: `1px solid ${theme.palette.primary.main}` }
              //   : undefined),
            }}
            id={`tree-item-${item.id}`}
            onClick={toggleOpen}
            ref={buttonRef}
            type="button"
            data-index={index}
            data-level={level}
            data-testid={`tree-item-${item.id}`}
          >
            {" "}
            {instruction ? (
              <DropIndicator instruction={instruction} position={"above"} />
            ) : null}
            <span
              style={{
                ...styles.innerButtonStyles,
                ...(state === "dragging"
                  ? styles.innerDraggingStyles
                  : state === "parent-of-instruction"
                  ? styles.parentOfInstructionStyles
                  : {}),
              }}
            >
              {/* <Icon item={item} /> */}
              <Icon item={item} />
              {item.type == "FILHO" ? (
                <WebRounded
                  sx={{ color: theme.palette.text.secondary, m: 1 }}
                  fontSize="small"
                />
              ) : null}
              <Typography sx={styles.labelStyles(item)}>
                {item.title}
              </Typography>
              <IconButton
                sx={{ color: theme.palette.text.secondary, fontSize: "1rem" }}
                size="small"
                onClick={handleClick}
              >
                <MoreHoriz />
              </IconButton>
              {/* <small style={styles.idStyles}>
                {item.isDraft ? <code>Draft</code> : null}
                <code style={styles.debugStyles}>({mode})</code>
              </small> */}
            </span>
            {instruction ? (
              <DropIndicator instruction={instruction} position={"below"} />
            ) : null}
          </button>
          {/* </Tooltip> */}
        </FocusRing>
      </Box>
      {item.children.length && item.isOpen ? (
        <Box
          id={aria?.["aria-controls"]}
          sx={{
            borderLeft: "1px solid " + theme.palette.text.disabled,
            marginLeft: 2,
            paddingLeft: "0.75rem",
          }}
        >
          {item.children.map((child, index, array) => {
            const childType: ItemMode = (() => {
              if (child.children.length && child.isOpen) {
                return "expanded";
              }

              if (index === array.length - 1) {
                return "last-in-group";
              }

              return "standard";
            })();
            return (
              <TreeItem
                item={child}
                key={child.id}
                level={level + 1}
                mode={childType}
                index={index}
                socket={socket}
                setExpanded={setExpanded}
                setUpdatePages={setUpdatePages}
                handleUpdateStatus={handleUpdateStatus}
              />
            );
          })}
        </Box>
      ) : null}
      <ModalTransition>
        {/* {isMoveDialogOpen && (
          <MoveDialog onClose={closeMoveDialog} itemId={item.id} />
        )} */}
        <MoveModal
          onClose={closeMoveDialog}
          itemId={item.id}
          open={isMoveDialogOpen}
          item={item}
          socket={socket}
        />
        <RenameModal
          onClose={closeRenameDialog}
          itemId={item.id}
          item={item}
          open={isRenameDialogOpen}
        />
        <NewItemModal
          onClose={closeNewItemDialog}
          open={isNewItemDialogOpen}
          position={item.position + "." + (item.children.length + 1)}
          parentId={item.id}
          type={type}
        />
        <NewAlertModal
          openModal={openDeleteDialog}
          setOpenModal={setOpenDeleteDialog}
          // confirmar = false
          handleButton={handleConfirmDelete}
          handleCancel={() => {
            setOpenDeleteDialog(false);
          }}
          severity="warning"
          textContent={`Você tem certeza que deseja excluir o item "${item.title}"?\n As páginas filhas serão movidas para a lixeira e os itens filhos serão excluídos permanentemente.`}
          singleButton={false}
          // confirmar={true}
          buttonText="Excluir"
          buttonCancelText="Cancelar"
        />
      </ModalTransition>
      <Menu
        id={`tree-item-${item.id}--menu`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: theme.palette.background.card,
            boxShadow: "0px 4px 14px 0px  #0000002c",
          },
        }}
      >
        {item.type === "PAI" ? (
          <>
            <MenuItem
              onClick={() => {
                openNewItemDialog("PAI");
              }}
            >
              <ListItemIcon>
                <AddCircleRounded fontSize="small" />
              </ListItemIcon>
              Adicionar Item
            </MenuItem>
            <MenuItem
              onClick={() => {
                openNewItemDialog("FILHO");
              }}
            >
              <ListItemIcon>
                <WebRounded fontSize="small" />
              </ListItemIcon>
              Adicionar Página
            </MenuItem>
          </>
        ) : null}
        {item.type === "FILHO" ? (
          <>
            <MenuItem onClick={handleButtonExport}>
              <ListItemIcon>
                <IosShareRounded fontSize="small" />
              </ListItemIcon>
              Exportar Página
            </MenuItem>
            {/* <MenuItem onClick={openMoveDialog}>
              <ListItemIcon>
                <ControlPointDuplicateRounded fontSize="small" />
              </ListItemIcon>
              Duplicar
            </MenuItem> */}
            <MenuItem onClick={handleRemoveItemTreeView}>
              <ListItemIcon>
                <OutputRounded fontSize="small" />
              </ListItemIcon>
              Remover da Barra de Navegação
            </MenuItem>
          </>
        ) : null}
        <MenuItem onClick={openMoveDialog}>
          <ListItemIcon>
            <ImportExportRounded fontSize="small" />
          </ListItemIcon>
          Mover
        </MenuItem>
        <MenuItem onClick={openRenameDialog}>
          <ListItemIcon>
            <EditRounded fontSize="small" />
          </ListItemIcon>
          Renomear
        </MenuItem>
        <Divider />
        {item.type === "PAI" ? (
          <MenuItem onClick={handleButtonDelete}>
            <ListItemIcon>
              <DeleteRounded color="error" fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.error.main }}>
              Exluir
            </ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={handleButtonDump}>
            <ListItemIcon>
              <DeleteRounded color="error" fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.error.main }}>
              Mover para a Lixeira
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Fragment>
  );
});

export default TreeItem;
